import { useLocation } from 'react-router-dom'

import {
  SERVICES_PATH,
  CATALOG_PATH,
  WORKSHOP_EVENTS_PATH,
  MECHANIC_EVENTS_PATH,
  DRIVER_PATH,
} from '@/utils/routePaths'

import queryClient from '@/services/queryClient'

import {
  AUTHORIZATION_COOKIE_KEY,
  REMEMBER_ME_COOKIE_KEY,
  REMEMBER_ME_COOKIE_EXPIRATION_DAYS,
  LOGIN_SESSION_DATA_LOCAL_STORAGE_KEY,
  SWITCH_SESSION_COOKIE_KEY,
} from '@/blocks/Auth/definitions'
import { clearTokenLoginFailed } from '@/blocks/Auth/tokenLoginGuard'

import { useAuthCookieManager } from './useAuthCookieManager'

const COOKIES_TO_REMOVE_ON_FORCE_LOGOUT = [AUTHORIZATION_COOKIE_KEY]

const COOKIES_TO_REMOVE_ON_MANUAL_LOGOUT = [
  AUTHORIZATION_COOKIE_KEY,
  SWITCH_SESSION_COOKIE_KEY,
  REMEMBER_ME_COOKIE_KEY,
]

export const useAuthDataHandler = () => {
  const { saveAuthCookie, removeAuthCookies, getAuthTokenFromCookie } =
    useAuthCookieManager()

  const saveAuthTokens = ({ jwtToken, loginToken, isRememberMeEnabled }) => {
    if (jwtToken)
      saveAuthCookie({ cookieName: AUTHORIZATION_COOKIE_KEY, token: jwtToken })
    if (loginToken) {
      saveAuthCookie({
        cookieName: isRememberMeEnabled
          ? REMEMBER_ME_COOKIE_KEY
          : SWITCH_SESSION_COOKIE_KEY,
        token: loginToken,
        temporary: false,
        expiresInDays: REMEMBER_ME_COOKIE_EXPIRATION_DAYS,
      })
    }
  }

  const saveLoginSessionData = (loginSessionData) => {
    const {
      entityID: entityId,
      entityName,
      employers,
      ...data
    } = loginSessionData
    const branches =
      employers?.filter((employer) => employer.entityId !== entityId) || []
    localStorage.setItem(
      LOGIN_SESSION_DATA_LOCAL_STORAGE_KEY,
      JSON.stringify({ entityId, entityName, branches, ...data }),
    )
  }

  const updateLoginSessionData = (newData) => {
    const existingData = getLoginSessionData()
    const updatedData = { ...existingData, ...newData }
    localStorage.setItem(
      LOGIN_SESSION_DATA_LOCAL_STORAGE_KEY,
      JSON.stringify(updatedData),
    )
  }

  const getLoginSessionData = () => {
    return JSON.parse(
      localStorage.getItem(LOGIN_SESSION_DATA_LOCAL_STORAGE_KEY),
    )
  }

  const clearLoginSessionData = () => {
    localStorage.removeItem(LOGIN_SESSION_DATA_LOCAL_STORAGE_KEY)
  }

  const clearUserData = (isJwtOnlyLogout) => {
    if (isJwtOnlyLogout) {
      removeAuthCookies(COOKIES_TO_REMOVE_ON_FORCE_LOGOUT)
    } else {
      removeAuthCookies(COOKIES_TO_REMOVE_ON_MANUAL_LOGOUT)
      clearLoginSessionData()
      clearTokenLoginFailed()
    }
    queryClient.removeQueries()
  }

  const getJwtToken = () => {
    return getAuthTokenFromCookie(AUTHORIZATION_COOKIE_KEY)
  }

  const getLoginToken = () => {
    return (
      getAuthTokenFromCookie(REMEMBER_ME_COOKIE_KEY) ??
      getAuthTokenFromCookie(SWITCH_SESSION_COOKIE_KEY)
    )
  }

  const isAltLoginToken = () =>
    !!getAuthTokenFromCookie(SWITCH_SESSION_COOKIE_KEY)

  const location = useLocation()

  const getDefaultHomePage = (services) => {
    const { catalog, files, workShop, driverReport } = services ?? {}
    if (files) return SERVICES_PATH
    if (workShop) {
      const isMechanicEvents = location.pathname
        .toLowerCase()
        .includes(MECHANIC_EVENTS_PATH.toLowerCase())
      return isMechanicEvents ? MECHANIC_EVENTS_PATH : WORKSHOP_EVENTS_PATH
    }
    if (catalog) return CATALOG_PATH
    if (driverReport) return DRIVER_PATH
    return SERVICES_PATH
  }

  const clearLoginTokens = () => {
    removeAuthCookies([REMEMBER_ME_COOKIE_KEY, SWITCH_SESSION_COOKIE_KEY])
    clearTokenLoginFailed()
  }

  return {
    saveAuthTokens,
    saveLoginSessionData,
    updateLoginSessionData,
    getLoginSessionData,
    clearLoginSessionData,
    clearUserData,
    clearLoginTokens,
    getJwtToken,
    getLoginToken,
    isAltLoginToken,
    getDefaultHomePage,
  }
}
